import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { ArrowUp, Loader2, Mic, MicOff, Plus, Square, X } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/services/api';
import { useChatStore } from '@/stores/chatStore';
import { useFileStore } from '@/stores/fileStore';
import { getPathName, isImageFile, toAttachmentFile } from './presentation';
import {
  discoverSlashCommands,
  toCanonicalSlashCommand,
  REMEMBER_SLASH_COMMAND,
  type SlashCommandSkill,
} from './slashCommandDiscovery';
import { createSpeechEngine, isSpeechRecognitionSupported } from './speech/speechEngine';
import {
  createVoiceInputController,
  voiceInputErrorMessage,
  VOICE_INPUT_UNSUPPORTED_MESSAGE,
  type VoiceInputController,
} from './speech/voiceInputController';

interface ChatComposerProps {
  onSend: (message: string, images?: string[]) => boolean | Promise<boolean>;
  onCancel: () => void;
  isStreaming: boolean;
  disabled?: boolean;
  provider: 'claude' | 'codex';
  skills: SlashCommandSkill[];
}

interface AttachedImage {
  path: string;
  name: string;
}

export function ChatComposer({
  onSend,
  onCancel,
  isStreaming,
  disabled,
  provider,
  skills,
}: ChatComposerProps) {
  const [message, setMessage] = useState('');
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [isSlashMenuDismissed, setIsSlashMenuDismissed] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isVoiceInputSupported] = useState(() => isSpeechRecognitionSupported());
  const voiceInputRef = useRef<VoiceInputController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const slashCommandMenuId = useId();
  const refreshTree = useFileStore((state) => state.refreshTree);

  const pendingComposerText = useChatStore((state) => state.pendingComposerText);
  const setPendingComposerText = useChatStore((state) => state.setPendingComposerText);
  const pendingComposerInsert = useChatStore((state) => state.pendingComposerInsert);
  const setPendingComposerInsert = useChatStore((state) => state.setPendingComposerInsert);
  const pendingComposerImages = useChatStore((state) => state.pendingComposerImages);
  const setPendingComposerImages = useChatStore((state) => state.setPendingComposerImages);

  const slashCommandMatches = useMemo(
    () => discoverSlashCommands([REMEMBER_SLASH_COMMAND, ...skills], message),
    [message, skills]
  );
  const showSlashCommandMenu =
    !isSlashMenuDismissed &&
    !isStreaming &&
    !disabled &&
    slashCommandMatches.length > 0;

  useEffect(() => {
    setSelectedCommandIndex(0);
  }, [message, skills]);

  const setDraft = useCallback((text: string) => {
    voiceInputRef.current?.rebase(text);
    setMessage(text);
  }, []);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, 28), 100)}px`;
  }, []);

  useEffect(() => {
    const controller = createVoiceInputController({
      engine: createSpeechEngine(),
      onText: (text) => {
        setMessage(text);
        requestAnimationFrame(adjustTextareaHeight);
      },
      onListeningChange: setIsListening,
      onError: (error) => {
        const description = voiceInputErrorMessage(error);
        if (description) toast.error('Voice input unavailable', { description });
      },
    });
    voiceInputRef.current = controller;
    return () => {
      controller.discard();
      voiceInputRef.current = null;
    };
  }, [adjustTextareaHeight]);

  useEffect(() => {
    if (isStreaming || disabled) voiceInputRef.current?.stop();
  }, [disabled, isStreaming]);

  useEffect(() => {
    if (!isListening) return;
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      voiceInputRef.current?.stop();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isListening]);

  const toggleVoiceInput = useCallback(() => {
    voiceInputRef.current?.toggle(textareaRef.current?.value ?? '');
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    if (pendingComposerText == null) return;
    const text = pendingComposerText;
    setDraft(text);
    setPendingComposerText(null);
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(text.length, text.length);
      adjustTextareaHeight();
    });
  }, [adjustTextareaHeight, pendingComposerText, setDraft, setPendingComposerText]);

  useEffect(() => {
    if (pendingComposerInsert == null) return;
    const insert = pendingComposerInsert;
    setPendingComposerInsert(null);
    const textarea = textareaRef.current;
    const start = textarea ? textarea.selectionStart : null;
    const end = textarea ? textarea.selectionEnd : null;
    const current = textarea?.value ?? '';
    setDraft(current.slice(0, start ?? current.length) + insert + current.slice(end ?? current.length));
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      const caret = start != null ? start + insert.length : node.value.length;
      node.setSelectionRange(caret, caret);
      adjustTextareaHeight();
    });
  }, [adjustTextareaHeight, pendingComposerInsert, setDraft, setPendingComposerInsert]);

  useEffect(() => {
    if (pendingComposerImages.length === 0) return;
    setAttachedImages((current) => {
      const existing = new Set(current.map((image) => image.path));
      const next = pendingComposerImages
        .filter((path) => !existing.has(path))
        .map((path) => ({ path, name: getPathName(path) }));
      return next.length > 0 ? [...current, ...next] : current;
    });
    setPendingComposerImages([]);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [pendingComposerImages, setPendingComposerImages]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setDraft(event.target.value);
      setIsSlashMenuDismissed(false);
      adjustTextareaHeight();
    },
    [adjustTextareaHeight, setDraft]
  );

  const selectSlashCommand = useCallback(
    (skill: SlashCommandSkill) => {
      const command = toCanonicalSlashCommand(skill);
      setDraft(command);
      setIsSlashMenuDismissed(true);
      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.focus();
        textarea.setSelectionRange(command.length, command.length);
        adjustTextareaHeight();
      });
    },
    [adjustTextareaHeight, setDraft]
  );

  const handleImageInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files ? Array.from(event.target.files) : [];
      event.target.value = '';
      if (selected.length === 0) return;

      const imageFiles = selected.filter(isImageFile);
      if (imageFiles.length !== selected.length) toast.error('Only image files can be attached to chat');
      if (imageFiles.length === 0) return;

      setIsUploadingImage(true);
      try {
        const result = await api.workspace.assets.upload(imageFiles.map(toAttachmentFile), 'Inbox');
        if (result.failed.length > 0) {
          toast.error('Some screenshots were not attached', {
            description: result.failed.map((file) => file.filename).join(', '),
          });
        }
        if (result.uploaded.length > 0) {
          setAttachedImages((current) => [
            ...current,
            ...result.uploaded.map((asset) => ({ path: asset.backingPath, name: asset.name })),
          ]);
          await refreshTree();
        }
      } catch (error) {
        toast.error('Failed to attach screenshot', {
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        setIsUploadingImage(false);
      }
    },
    [refreshTree]
  );

  const handleSend = useCallback(async () => {
    const trimmedMessage = message.trim();
    const imagePaths = attachedImages.map((image) => image.path);
    if (
      (!trimmedMessage && imagePaths.length === 0) ||
      isStreaming ||
      disabled ||
      isUploadingImage ||
      isSubmitting
    ) {
      return;
    }

    voiceInputRef.current?.discard();
    setIsSubmitting(true);
    const accepted = await onSend(trimmedMessage || 'Please inspect the attached image(s).', imagePaths);
    if (!accepted) {
      setIsSubmitting(false);
      return;
    }

    setMessage('');
    setAttachedImages([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [attachedImages, disabled, isStreaming, isSubmitting, isUploadingImage, message, onSend]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (showSlashCommandMenu) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedCommandIndex((current) => (current + 1) % slashCommandMatches.length);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedCommandIndex((current) =>
            current === 0 ? slashCommandMatches.length - 1 : current - 1
          );
          return;
        }
        if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
          event.preventDefault();
          const selectedCommand = slashCommandMatches[selectedCommandIndex];
          if (selectedCommand) selectSlashCommand(selectedCommand);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setIsSlashMenuDismissed(true);
          return;
        }
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void handleSend();
      }
    },
    [handleSend, selectSlashCommand, selectedCommandIndex, showSlashCommandMenu, slashCommandMatches]
  );

  useEffect(() => {
    if (!isStreaming) textareaRef.current?.focus();
  }, [isStreaming]);

  useEffect(() => {
    if (isStreaming || (message.trim().length === 0 && attachedImages.length === 0)) {
      setIsSubmitting(false);
    }
  }, [attachedImages.length, isStreaming, isSubmitting, message]);

  const canSend =
    (message.trim().length > 0 || attachedImages.length > 0) &&
    !isStreaming &&
    !disabled &&
    !isUploadingImage &&
    !isSubmitting;

  return (
    <div className="border-t border-surface-200 p-4 dark:border-surface-800">
      <div className="relative flex flex-col gap-1.5 overflow-hidden rounded-[14px] border border-surface-300 bg-white p-1.5 shadow-sm transition-[border-color,box-shadow] focus-within:border-surface-400 focus-within:shadow-md dark:border-surface-700 dark:bg-surface-900 dark:focus-within:border-surface-600">
        <input
          ref={imageInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/bmp"
          multiple
          className="hidden"
          onChange={handleImageInputChange}
        />

        {showSlashCommandMenu && (
          <div
            id={slashCommandMenuId}
            role="listbox"
            aria-label="Slash commands"
            className="max-h-64 overflow-y-auto border-b border-surface-200 pb-1.5 dark:border-surface-700"
          >
            <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-surface-500 dark:text-surface-400">
              Commands
            </div>
            {slashCommandMatches.map((skill, index) => {
              const isSelected = index === selectedCommandIndex;
              const optionId = `${slashCommandMenuId}-${index}`;
              return (
                <button
                  key={skill.name}
                  id={optionId}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => selectSlashCommand(skill)}
                  onMouseEnter={() => setSelectedCommandIndex(index)}
                  className={`block w-full rounded-lg px-2 py-1.5 text-left transition-colors ${
                    isSelected
                      ? 'bg-surface-100 dark:bg-surface-800'
                      : 'hover:bg-surface-50 dark:hover:bg-surface-800/60'
                  }`}
                >
                  <span className="flex min-w-0 items-baseline gap-2">
                    <code className="shrink-0 text-xs font-semibold text-primary-700 dark:text-primary-300">
                      /{skill.name}
                    </code>
                    <span className="truncate text-xs font-medium text-surface-800 dark:text-surface-200">
                      {skill.title}
                    </span>
                  </span>
                  <span className="mt-0.5 block truncate text-[11px] text-surface-500 dark:text-surface-400">
                    {skill.description}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {attachedImages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-1 pt-0.5">
            {attachedImages.map((image) => {
              const url = api.files.getDownloadUrl(image.path, true);
              return (
                <div
                  key={image.path}
                  className="group relative h-12 w-16 overflow-hidden rounded-lg border border-surface-200 bg-surface-100 shadow-sm dark:border-surface-700 dark:bg-surface-800"
                  title={image.path}
                >
                  <img src={url} alt={image.name} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setAttachedImages((current) => current.filter((item) => item.path !== image.path))}
                    className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity hover:bg-black/85 group-hover:opacity-100 focus:opacity-100"
                    aria-label={`Remove ${image.name}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                  <span className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1 py-0.5 text-[9px] text-white">
                    {image.name}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-[28px_minmax(0,1fr)_28px_28px] items-end gap-1">
          <button
            type="button"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-surface-500 transition-[background-color,color,transform] hover:bg-surface-100 hover:text-surface-900 active:scale-[0.94] disabled:cursor-not-allowed disabled:opacity-40 dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-100"
            disabled={isStreaming || disabled || isUploadingImage}
            title="Attach screenshot"
            aria-label="Attach screenshot"
            onClick={() => imageInputRef.current?.click()}
          >
            {isUploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </button>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={isStreaming ? `${provider === 'codex' ? 'Codex' : 'Claude'} is responding...` : 'Type your message...'}
            disabled={isStreaming || disabled}
            rows={1}
            aria-label="Prompt"
            aria-autocomplete="list"
            aria-controls={showSlashCommandMenu ? slashCommandMenuId : undefined}
            aria-expanded={showSlashCommandMenu}
            aria-activedescendant={
              showSlashCommandMenu ? `${slashCommandMenuId}-${selectedCommandIndex}` : undefined
            }
            className="min-h-7 w-full min-w-0 resize-none bg-transparent px-1 py-[5px] text-[13px] leading-[18px] text-surface-900 outline-none [overflow-wrap:anywhere] placeholder:text-surface-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-surface-100 dark:placeholder:text-surface-500"
            style={{ maxHeight: '100px' }}
          />

          <span className="flex" title={isVoiceInputSupported ? undefined : VOICE_INPUT_UNSUPPORTED_MESSAGE}>
            <button
              type="button"
              onClick={toggleVoiceInput}
              disabled={!isVoiceInputSupported || isStreaming || disabled}
              aria-pressed={isListening}
              aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
              title={
                isVoiceInputSupported
                  ? isListening
                    ? 'Stop voice input (Esc)'
                    : 'Start voice input'
                  : VOICE_INPUT_UNSUPPORTED_MESSAGE
              }
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-[background-color,color,transform] active:scale-[0.94] disabled:cursor-not-allowed disabled:opacity-40 ${
                isListening
                  ? 'animate-pulse bg-red-600 text-white hover:bg-red-700'
                  : 'text-surface-500 hover:bg-surface-100 hover:text-surface-900 dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-100'
              }`}
            >
              {isListening || !isVoiceInputSupported ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          </span>

          {isStreaming ? (
            <button
              type="button"
              onClick={onCancel}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white transition-transform hover:bg-red-700 active:scale-[0.94]"
              title="Stop generating"
              aria-label="Stop generating"
            >
              <Square className="h-3 w-3 fill-current" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!canSend}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface-900 text-white transition-[background-color,color,transform] enabled:hover:bg-surface-700 enabled:active:scale-[0.94] disabled:cursor-not-allowed disabled:bg-surface-200 disabled:text-surface-500 dark:bg-surface-100 dark:text-surface-900 dark:enabled:hover:bg-white dark:disabled:bg-surface-700 dark:disabled:text-surface-500"
              title="Send message (Enter)"
              aria-label="Send message (Enter)"
            >
              <ArrowUp className="h-4 w-4" strokeWidth={2.4} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
